/**
 * AURA Profiles Module
 * Manages accessibility profiles activation/deactivation
 * Supports linked profiles that toggle together as a pair.
 */

import {
  AuraSettings,
  ProfileName,
  PartialAuraSettings,
  ProfileDefinition,
  createDefaultSettings,
} from '../../core/types';
import { stateManager } from '../../core/state';
import { eventBus } from '../../core/events';
import { Logger } from '../../core/logger';
import { playActivationDing, playDeactivationTone } from '../../core/audio';
import { createProfileButton } from '../../ui/controls';
import { getIcon } from '../../ui/styles/icons';
import { PROFILE_DEFINITIONS, getProfileDefinition, getAllProfileNames } from './definitions';

export { PROFILE_DEFINITIONS, getProfileDefinition, getAllProfileNames };

const logger = new Logger('ProfilesModule');

interface ProfileButtonRef {
  name: ProfileName;
  linkedWith?: ProfileName;
  setActive: (active: boolean) => void;
}

const profileButtonRefs: ProfileButtonRef[] = [];

export const LINKED_PROFILE_STYLES = `
  .aura-linked-group {
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .aura-linked-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px 0;
    color: var(--aura-text-muted);
  }

  .aura-linked-indicator svg {
    width: 16px;
    height: 16px;
    opacity: 0.6;
  }
`;

/**
 * Determine whether a profile should appear active based on
 * the current active profile and linked profile relationships.
 */
function isProfileActive(profileName: ProfileName, activeProfile: ProfileName | null): boolean {
  if (!activeProfile) return false;
  if (activeProfile === profileName) return true;
  const activeDef = getProfileDefinition(activeProfile);
  return activeDef?.linkedWith === profileName;
}

function createToggleHandler(profileName: ProfileName) {
  return (active: boolean) => {
    if (active) {
      activateProfile(profileName);
    } else {
      deactivateProfile();
    }
  };
}

function createLinkedProfileGroup(
  name1: ProfileName,
  def1: ProfileDefinition,
  name2: ProfileName,
  def2: ProfileDefinition,
): HTMLElement {
  const activeProfile = stateManager.getState().activeProfile;
  const groupActive = isProfileActive(name1, activeProfile) || isProfileActive(name2, activeProfile);

  const group = document.createElement('div');
  group.className = 'aura-linked-group';
  group.setAttribute('role', 'group');
  group.setAttribute('aria-label', 'Linked accessibility profiles');

  const btn1 = createProfileButton({
    id: `aura-profile-${name1}`,
    name: def1.name,
    description: def1.description,
    icon: def1.icon,
    active: groupActive,
    onToggle: createToggleHandler(name1),
  });
  profileButtonRefs.push({ name: name1, linkedWith: name2, setActive: btn1.setActive });

  const indicator = document.createElement('div');
  indicator.className = 'aura-linked-indicator';
  indicator.setAttribute('aria-hidden', 'true');
  indicator.innerHTML = getIcon('linkChain');

  const btn2 = createProfileButton({
    id: `aura-profile-${name2}`,
    name: def2.name,
    description: def2.description,
    icon: def2.icon,
    active: groupActive,
    onToggle: createToggleHandler(name2),
  });
  profileButtonRefs.push({ name: name2, linkedWith: name1, setActive: btn2.setActive });

  group.appendChild(btn1.element);
  group.appendChild(indicator);
  group.appendChild(btn2.element);

  return group;
}

/**
 * Initialize the profiles module
 */
export function initProfilesModule(container: HTMLElement): void {
  logger.info('Initializing profiles module');

  const profilesContainer = container.querySelector('#aura-profiles-container');
  if (!profilesContainer) {
    logger.warn('Profiles container not found');
    return;
  }

  profilesContainer.innerHTML = '';
  profileButtonRefs.length = 0;

  const rendered = new Set<ProfileName>();

  getAllProfileNames().forEach((profileName) => {
    if (rendered.has(profileName)) return;

    const definition = getProfileDefinition(profileName);
    if (!definition) return;

    const linkedName = definition.linkedWith;
    const linkedDef = linkedName ? getProfileDefinition(linkedName) : undefined;

    if (linkedName && linkedDef) {
      profilesContainer.appendChild(
        createLinkedProfileGroup(profileName, definition, linkedName, linkedDef),
      );
      rendered.add(profileName);
      rendered.add(linkedName);
      return;
    }

    const { element, setActive } = createProfileButton({
      id: `aura-profile-${profileName}`,
      name: definition.name,
      description: definition.description,
      icon: definition.icon,
      active: stateManager.getState().activeProfile === profileName,
      onToggle: createToggleHandler(profileName),
    });

    profileButtonRefs.push({ name: profileName, setActive });
    profilesContainer.appendChild(element);
    rendered.add(profileName);
  });

  stateManager.subscribe((state) => {
    profileButtonRefs.forEach((ref) => {
      ref.setActive(isProfileActive(ref.name, state.activeProfile));
    });
  });

  logger.info('Profiles module initialized', {
    profileCount: getAllProfileNames().length,
  });
}

/**
 * Activate a profile (and its linked partner visually via state subscription).
 * Only one sound is played regardless of linked profiles.
 */
export function activateProfile(profileName: ProfileName): void {
  const definition = getProfileDefinition(profileName);
  if (!definition) {
    logger.warn(`Profile not found: ${profileName}`);
    return;
  }

  if (stateManager.getState().activeProfile) {
    deactivateProfile();
  }

  const profileSettings: PartialAuraSettings = {
    content: definition.settings.content,
    color: definition.settings.color,
    orientation: definition.settings.orientation,
  };

  stateManager.activateProfile(profileName, profileSettings);

  if (definition.playSound) {
    playActivationDing();
  }

  if (profileName === 'screenReader' || definition.linkedWith === 'screenReader') {
    eventBus.emit('remediation:start');
  }

  logger.info(`Profile activated: ${profileName}`);
}

/**
 * Compute the settings to apply when a profile is being deactivated.
 *
 * Reset every key the profile owns back to its DEFAULT (off) value, while
 * preserving every other setting the user may have changed during the
 * profile's reign.  This is what users expect from "turn the profile off":
 * the profile's effects go away cleanly even if some of those keys happened
 * to be on independently before the user activated the profile.
 */
function computeSettingsAfterDeactivation(
  current: AuraSettings,
  definition: ProfileDefinition,
): AuraSettings {
  const defaults = createDefaultSettings();
  const next: AuraSettings = {
    content: { ...current.content },
    color: { ...current.color },
    orientation: { ...current.orientation },
  };
  const sections = ['content', 'color', 'orientation'] as const;
  for (const section of sections) {
    const profileSection = definition.settings[section] as
      | Record<string, unknown>
      | undefined;
    if (!profileSection) continue;
    const target = next[section] as unknown as Record<string, unknown>;
    const defaultsForSection = defaults[section] as unknown as Record<string, unknown>;
    for (const key of Object.keys(profileSection)) {
      target[key] = defaultsForSection[key];
    }
  }
  return next;
}

/**
 * Deactivate current profile.
 * Only one deactivation sound is played regardless of linked profiles.
 */
export function deactivateProfile(): void {
  const current = stateManager.getState().activeProfile;
  if (!current) return;
  const definition = getProfileDefinition(current);

  if (definition) {
    const restored = computeSettingsAfterDeactivation(
      stateManager.getSettings() as AuraSettings,
      definition,
    );
    stateManager.deactivateProfile(restored);
  } else {
    stateManager.deactivateProfile();
  }

  if (definition?.playSound) {
    playDeactivationTone();
  }

  logger.info('Profile deactivated');
}

/**
 * Toggle a profile on/off
 */
export function toggleProfile(profileName: ProfileName): void {
  const currentProfile = stateManager.getState().activeProfile;

  if (currentProfile === profileName) {
    deactivateProfile();
  } else {
    activateProfile(profileName);
  }
}

/**
 * Get currently active profile
 */
export function getActiveProfile(): ProfileName | null {
  return stateManager.getState().activeProfile;
}
